/**
 * VerificationCoordinator - Coordinates toast → modal handoff for verification
 * 
 * This component bridges the gap between:
 * - verificationRequestManager (handles incoming requests, shows toasts)
 * - VerificationModal (handles the actual verification UI)
 * 
 * It must be rendered within ToastProvider context to work properly.
 */

import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import verificationRequestManager from '../../services/verificationRequestManager';
import matrixService from '../../services/matrixService';
import VerificationModal from './VerificationModal';
import logger from '../../utils/logger';

const VerificationCoordinator = () => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    request: null,
    requestId: null,
    displayName: null,
  });

  const toastContext = useToast();

  /**
   * Handle user clicking "Verify" in toast
   * Opens the verification modal AND accepts the request
   */
  const handleVerifyRequest = useCallback(async (request, requestId) => {
    logger.info('User clicked Verify button, opening verification modal');

    // Get device name for display
    const deviceName = await getDeviceName(request);

    // IMPORTANT: Accept the request only - don't start verification
    // The INITIATOR will start verification, responder just accepts
    // This matches Element Web's flow for responding to verification requests
    try {
      await request.accept();
    } catch (error) {
      logger.error('VerificationCoordinator: Failed to accept request:', error);
      // Still open modal - it will show the error
    }

    setModalState({
      isOpen: true,
      request,
      requestId,
      displayName: deviceName,
    });

    // Dismiss the toast since we're opening the modal
    toastContext.dismissToast(requestId);
  }, [toastContext]);

  // Initialize verification request manager with contexts
  useEffect(() => {
    logger.debug('VerificationCoordinator: Initializing');

    // Initialize the verification request manager with toast context
    verificationRequestManager.initialize(
      toastContext,
      null, // modalContext not needed with callback approach
      matrixService,
    );

    // Set callback for when user clicks "Verify" in toast
    verificationRequestManager.setOnVerifyRequest(handleVerifyRequest);

    logger.info('VerificationCoordinator: Initialized and ready');

    return () => {
      // Cleanup on unmount
      verificationRequestManager.setOnVerifyRequest(null);
    };
  }, [toastContext, handleVerifyRequest]);

  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    logger.debug('VerificationCoordinator: Modal closed');
    
    const { requestId } = modalState;
    
    setModalState({
      isOpen: false,
      request: null,
      requestId: null,
      displayName: null,
    });

    // Clean up the request from active requests
    if (requestId && verificationRequestManager.activeRequests) {
      verificationRequestManager.activeRequests.delete(requestId);
    }
  };

  /**
   * Handle verification complete
   */
  const handleVerificationComplete = (success) => {
    logger.info('VerificationCoordinator: Verification completed:', success);
    
    const { requestId } = modalState;

    // Clean up
    if (requestId && verificationRequestManager.activeRequests) {
      verificationRequestManager.activeRequests.delete(requestId);
    }

    // Close modal after a brief delay to show success state
    setTimeout(() => {
      handleModalClose();
    }, 1500);
  };

  /**
   * Get device name from request
   */
  const getDeviceName = async (request) => {
    try {
      const client = matrixService.client;
      if (!client) {
        return 'Unknown Device';
      }

      const crypto = client.getCrypto?.();
      if (!crypto) {
        return 'Unknown Device';
      }

      const userId = request.otherUserId || request.userId;
      const deviceId = request.otherDeviceId || request.deviceId;

      if (!userId || !deviceId) {
        return 'Unknown Device';
      }

      // Try to get device info using correct Rust crypto API
      const userDevices = await crypto.getUserDeviceInfo?.([userId]);
      const device = userDevices?.get?.(userId)?.get?.(deviceId);
      
      if (device?.display_name || device?.displayName) {
        return device.display_name || device.displayName;
      }

      // Fallback to device ID
      return `Device ${deviceId.substring(0, 8)}`;
    } catch (error) {
      logger.warn('VerificationCoordinator: Could not get device name:', error);
      return 'Unknown Device';
    }
  };

  // Render the verification modal
  // CRITICAL: Use requestId as key to force new component instance per request
  // This ensures complete cleanup of old verification state and listeners
  return (
    <VerificationModal
      key={modalState.requestId || 'closed'}
      isOpen={modalState.isOpen}
      incomingRequest={modalState.request}
      verificationType="device"
      displayName={modalState.displayName}
      onClose={handleModalClose}
      onComplete={handleVerificationComplete}
    />
  );
};

export default VerificationCoordinator;
